import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { chipBoxClass, FilterBarDensity, useFilterBarDensity } from './filterBarDensity';
import { useTranslation } from 'react-i18next';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { shortDateFormat, stringIsoFromDateInTz, stringYMDFromDate } from '@utils/dateWithTz';
import { toBoolean } from '@app/pages/customer/supplier-notification/utils.ts';
import SvgChevron from '@icons/chevron.svg?react';
import SvgClose from '@icons/close.svg?react';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip';
import { DateTimeFormatType, FilterConfig, FilterType } from '../types';
import { clearFilter, FILTER_APPLY_DELAY_MS, getFilterKey, getFilterLabel, getFilterValueLabel, isFilterActive, renderChipValue, sameValue } from './filterChipUtils';

interface Props {
  conf: FilterConfig;
  filter: any;
  applyFilterValue: (key: string, value: any) => void;
  loading: boolean;
  /** Open the editor automatically on mount (used for chips just added via "Add filter"). */
  autoOpen?: boolean;
  /** Called when the editor closes; `active` reflects whether a value ended up set. */
  onClosed?: (active: boolean) => void;
  /** Let the value wrap to multiple lines instead of truncating (used in the "All filters" sidebar). */
  wrap?: boolean;
  /** Hide the field label inside the chip (the sidebar row already shows it). */
  hideLabel?: boolean;
}

const readKey = (filter: any, key: string) => (filter && typeof filter === 'object' && key in filter ? filter[key] : undefined);

const chipClass = (active: boolean, wrap: boolean | undefined, density: FilterBarDensity) =>
  clsx(
    "inline-flex items-center gap-1.5 rounded border border-solid font-['DM_Sans'] font-normal text-content-primary cursor-pointer select-none",
    chipBoxClass(density),
    active ? 'border-content-brand bg-content-brandMedium' : 'border-content-brandMedium bg-content-negativePrimary',
    wrap && 'min-w-0',
  );

/**
 * Filter chip for the non-select filter types. InputText / Calendar open their editor in a panel below
 * the chip (same as the select chips' panels); Checkbox toggles directly. Used by both the filter bar
 * and the "All filters" sidebar (via FilterChipControl) so behaviour is identical.
 */
export const FilterChip: React.FC<Props> = props => (props.conf.filterType === FilterType.Checkbox ? <CheckboxChip {...props} /> : <PanelChip {...props} />);

/** Chip whose editor (InputText or inline Calendar) lives in an OverlayPanel anchored below the chip. */
const PanelChip: React.FC<Props> = ({ conf, filter, applyFilterValue, autoOpen, onClosed, wrap, hideLabel }) => {
  const density = useFilterBarDensity();
  const { tr } = useTranslationLgs();
  const op = useRef<OverlayPanel>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const label = getFilterLabel(conf);

  // Draft drives the chip + editor and updates immediately on every edit. The real filter (API) is applied
  // on a debounce (fires even while open), only for keys that actually changed; a still-pending change is
  // flushed on close so it isn't lost (no double-apply since the debounce clears `pending`).
  const initDraft = () => Object.fromEntries(conf.value.map(k => [k, readKey(filter, k)]));
  const [draftValues, setDraftValues] = useState<Record<string, any>>(initDraft);
  const draftRef = useRef(draftValues);
  draftRef.current = draftValues;
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const applyTimer = useRef<ReturnType<typeof setTimeout>>();
  const pending = useRef(false);

  const draftFilter = { ...filter, ...draftValues };
  // The chip reflects the draft right away (not the applied filter).
  const active = isFilterActive(conf, draftFilter);
  const valueLabel = active ? getFilterValueLabel(conf, draftFilter, tr) : '';

  // Re-sync the draft whenever the filter changes from outside (reset, saved filter, clear ✕).
  const filterSig = JSON.stringify(conf.value.map(k => readKey(filter, k)));
  useEffect(() => {
    setDraftValues(initDraft());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSig]);
  useEffect(() => () => clearTimeout(applyTimer.current), []);

  const commitChanged = () => {
    conf.value.forEach(k => {
      if (!sameValue(draftRef.current[k], readKey(filterRef.current, k))) applyFilterValue(k, draftRef.current[k]);
    });
  };
  const updateDraft = (key: string, value: any) => {
    setDraftValues(prev => ({ ...prev, [key]: value })); // chip + editor update immediately
    pending.current = true;
    clearTimeout(applyTimer.current);
    applyTimer.current = setTimeout(() => {
      pending.current = false;
      commitChanged();
    }, FILTER_APPLY_DELAY_MS);
  };
  const clearNow = () => {
    clearTimeout(applyTimer.current);
    pending.current = false;
    // clearFilter applies the empty value(s) immediately; the filterSig effect then re-syncs the draft.
    clearFilter(conf, applyFilterValue);
  };

  useEffect(() => {
    if (autoOpen && anchorRef.current) {
      op.current?.show({ currentTarget: anchorRef.current } as any, anchorRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        ref={anchorRef}
        className={chipClass(active, wrap, density)}
        onClick={e => op.current?.toggle(e, anchorRef.current)}
        data-testid={`filter-chip-${getFilterKey(conf)}`}
      >
        {!hideLabel && <span className="overflow-hidden text-ellipsis whitespace-nowrap font-normal">{label}</span>}
        {active ? (
          <span className={clsx(wrap ? 'min-w-0 break-words' : 'max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap')}>{renderChipValue(valueLabel)}</span>
        ) : (
          hideLabel && <span className="whitespace-nowrap font-normal text-content-secondary">{tr('RingilDataTable.notSelected', 'Not selected')}</span>
        )}
        <SvgChevron className={clsx('w-3 h-3 shrink-0 text-content-brand transition-transform', open && 'rotate-180')} />
        {active && (
          <>
            <span className="shrink-0 text-content-tertiary">|</span>
            <SvgClose
              className="w-3 h-3 shrink-0 text-complementary-error"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                clearNow();
              }}
              data-testid={`filter-chip-clear-${getFilterKey(conf)}`}
            />
          </>
        )}
      </div>
      {active && !open && <CustomTooltip targetSelector={`[data-testid="filter-chip-${getFilterKey(conf)}"]`} content={valueLabel} position="bottom" />}
      <OverlayPanel
        ref={op}
        className="filter-chip-overlay"
        onShow={() => {
          setOpen(true);
          // preventScroll on every focus: the panel is portaled to <body>, so a plain focus() makes the
          // browser (esp. Edge) scroll the document down to the portaled element, leaving an unrecoverable
          // blank gap. Calendar → focus the focusable day for arrow-key nav; text → focus the input
          // (replaces InputText's autoFocus, which can't take preventScroll).
          requestAnimationFrame(() => {
            if (conf.filterType === FilterType.Calendar) {
              const dp = document.querySelector('.filter-chip-overlay .p-datepicker');
              const day = dp?.querySelector<HTMLElement>('[tabindex="0"]') ?? dp?.querySelector<HTMLElement>('.p-datepicker-calendar td > span:not(.p-disabled)');
              day?.focus({ preventScroll: true });
            } else {
              document.querySelector<HTMLInputElement>('.filter-chip-overlay input')?.focus({ preventScroll: true });
            }
          });
        }}
        onHide={() => {
          setOpen(false);
          // Flush a still-pending change on a quick close; if the debounce already fired, don't refresh again.
          if (pending.current) {
            clearTimeout(applyTimer.current);
            pending.current = false;
            commitChanged();
          }
          onClosed?.(isFilterActive(conf, draftFilter));
        }}
        dismissable
        showCloseIcon={false}
      >
        {/* Enter confirms + closes (the calendar day's own Enter-select fires first as it bubbles up).
            Space selects the focused calendar day (PrimeReact's day keydown handles Enter but not Space). */}
        <div
          onKeyDown={e => {
            if (e.key === 'Enter') {
              op.current?.hide();
            } else if (e.key === ' ' && (e.target as HTMLElement).closest('.p-datepicker-calendar')) {
              e.preventDefault();
              (e.target as HTMLElement).click();
            }
          }}
        >
          <FilterChipEditor conf={conf} filter={draftFilter} applyFilterValue={updateDraft} />
        </div>
      </OverlayPanel>
    </>
  );
};

/** The actual input rendered inside the panel — a text box or an inline date picker. */
const FilterChipEditor: React.FC<{ conf: FilterConfig; filter: any; applyFilterValue: (key: string, value: any) => void }> = ({ conf, filter, applyFilterValue }) => {
  const { i18n } = useTranslation();

  if (conf.filterType === FilterType.Calendar) {
    const isSingle = conf.value.length === 1;
    const from = readKey(filter, conf.value[0]);
    const to = conf.value.length > 1 ? readKey(filter, conf.value[1]) : undefined;

    // Shared date helpers — no timezone conversion for date-only values (avoids the one-day shift).
    const fmt = (date: Date) => (conf.dateTimeFormat === DateTimeFormatType.ISOString ? stringIsoFromDateInTz(date, 'Europe/Prague') : stringYMDFromDate(date));

    const handleChange = (e: any) => {
      if (isSingle) {
        applyFilterValue(conf.value[0], e.value ? fmt(e.value as Date) : null);
        return;
      }
      if (!e.value || !Array.isArray(e.value)) {
        applyFilterValue(conf.value[0], null);
        if (conf.value[1]) applyFilterValue(conf.value[1], null);
        return;
      }
      const dates = e.value.filter((d: Date | null) => d !== null);
      applyFilterValue(conf.value[0], dates.length >= 1 ? fmt(dates[0]) : null);
      if (conf.value[1]) applyFilterValue(conf.value[1], dates.length >= 2 ? fmt(dates[1]) : null);
    };

    return (
      <Calendar
        inline
        showButtonBar
        locale={i18n.language}
        selectionMode={isSingle ? 'single' : 'range'}
        value={isSingle ? (from ? new Date(from) : null) : ([from ? new Date(from) : null, to ? new Date(to) : null].filter(Boolean) as Date[])}
        onChange={handleChange}
        dateFormat={shortDateFormat}
      />
    );
  }

  // Default: free-text input.
  return (
    <div className="w-[260px]">
      {/* Focus is managed by PanelChip.onShow with preventScroll (autoFocus can't take that option
          and would scroll the portaled panel into view, jumping the page in Edge). */}
      <InputText
        value={readKey(filter, conf.value[0]) || ''}
        onChange={e => applyFilterValue(conf.value[0], (e.target as HTMLInputElement).value)}
        placeholder={conf.placeholder}
        className="w-full"
        data-testid={conf.dataTestId}
      />
    </div>
  );
};

/** Boolean filter — the chip itself toggles the value; active state shows the brand styling + Yes/No. */
const CheckboxChip: React.FC<Props> = ({ conf, filter, applyFilterValue, autoOpen, onClosed, wrap, hideLabel }) => {
  const { tr } = useTranslationLgs();
  const density = useFilterBarDensity();
  const checked = toBoolean(readKey(filter, conf.value[0]));
  const label = getFilterLabel(conf);

  useEffect(() => {
    // Adding a boolean filter from "Add filter" activates it straight away (it has no panel to open).
    if (autoOpen) {
      if (!checked) applyFilterValue(conf.value[0], true);
      onClosed?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <button
      type="button"
      onClick={() => applyFilterValue(conf.value[0], !checked)}
      className={chipClass(checked, wrap, density)}
      data-testid={`filter-chip-${getFilterKey(conf)}`}
    >
      {!hideLabel && <span className="whitespace-nowrap font-normal">{label}</span>}
      <span className="font-bold">{checked ? tr('global.yes', 'Yes') : tr('global.no', 'No')}</span>
    </button>
  );
};
